import React from 'react';
import { Alert, Box, Stack, Typography } from '@mui/material';
import BieEntryDataGrid from './BieEntryDataGrid';
import BieEntryFilterBar from './BieEntryFilterBar';
import {
  fetchBieScenarioCombo,
  fetchBieSourceCombo,
  fetchBiePeriodCombo,
  fetchBieEntityCombo,
  fetchBieLocationCombo,
  fetchBieDriverCombo,
  fetchBieEntryTypeCombo,
  submitBieEntries,
  fetchBieAccountCombo,
} from './bieApiModern';

type State = {
  scenarioCode: string;
  sourceCode: string;
  filename: string;
  rows: any[];
  lookups: any;
  loading: boolean;
  error: string;
  success: string;
};
class BieEntryWorkbench extends React.Component<any, State> {
  state: State = {
    scenarioCode: 'DEMO01',
    sourceCode: 'EXCEL',
    filename: 'manual_grid_entry.json',
    rows: [],
    lookups: {
      scenarios: [],
      sources: [],
      periods: [],
      entities: [],
      locations: [],
      drivers: [],
      entryTypes: [],
      accounts: [],
    },
    loading: false,
    error: '',
    success: '',
  };
  componentDidMount() {
    this.loadLookups();
  }
  loadLookups = async () => {
    this.setState({ loading: true, error: '' });
    try {
      const [
        scenarios,
        sources,
        periods,
        entities,
        locations,
        accounts,
        drivers,
        entryTypes,
      ] = await Promise.all([
        fetchBieScenarioCombo(),
        fetchBieSourceCombo(),
        fetchBiePeriodCombo(),
        fetchBieEntityCombo(),
        fetchBieLocationCombo(),
        fetchBieAccountCombo(),
        fetchBieDriverCombo(),
        fetchBieEntryTypeCombo(),
      ]);
      this.setState({
        lookups: {
          ...this.state.lookups,
          scenarios,
          sources,
          periods,
          entities,
          locations,
          accounts,
          drivers,
          entryTypes,
        },
      });
    } catch (e: any) {
      this.setState({ error: e.message || String(e) });
    } finally {
      this.setState({ loading: false });
    }
  };
  dirtyCount = () =>
    (this.state.rows || []).filter((r: any) => r._state && r._state !== 'clean')
      .length;
  clear = () => this.setState({ rows: [], success: 'Entry grid cleared.' });
  addRow = () =>
    this.setState({
      rows: [
        ...this.state.rows,
        {
          id: `bie-entry-${Date.now()}`,
          eti: '',
          pdi: '',
          eni: '',
          lci: '',
          aci: '',
          dri: '',
          ref: '',
          qty: 0,
          upr: 0,
          amt: 0,
          dsc: '',
          _state: 'new',
        },
      ],
    });
  submit = async (rows: any[]) => {
    const dirty = rows.filter(
      (r: any) => r._state && r._state !== 'clean' && r._state !== 'deleted'
    );
    this.setState({ loading: true, error: '', success: '' });
    try {
      await submitBieEntries({
        trk: null,
        soi: null,
        sci: null,
        soc: this.state.sourceCode,
        scc: this.state.scenarioCode,
        rcd: dirty.map((r: any) => ({ scc: this.state.scenarioCode, ...r })),
        fnm: this.state.filename,
        fdt: JSON.stringify({}),
        att: JSON.stringify({}),
        exi: JSON.stringify({ origin: 'FDataGrid' }),
        rmk: 'Manual editable DataGrid import',
      });
      this.setState({
        success: `${dirty.length} BIE entry row(s) submitted.`,
        rows: rows.map((r: any) => ({ ...r, _state: 'clean' })),
      });
    } catch (e: any) {
      this.setState({ error: e.message || String(e) });
    } finally {
      this.setState({ loading: false });
    }
  };
  render() {
    const s = this.state;
    const { scenarioCode, sourceCode, filename, lookups, loading, rows } = this.state;
    const {scenarios,sources} = lookups;

    return (
      <Box sx={{ p: 3 }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              BIE Editable Entry Workbench
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manual BIE data capture using editable DataGrid and controlled
              lookup fields.
            </Typography>
          </Box>
          <BieEntryFilterBar
            scenarioCode={scenarioCode}
            sourceCode={sourceCode}
            filename={filename}
            scenarios={scenarios}
            sources={sources}
            loading={loading}
            dirtyCount={this.dirtyCount()}
            onScenarioChange={(scenarioCode) => this.setState({ scenarioCode })}
            onSourceChange={(sourceCode) => this.setState({ sourceCode })}
            onFilenameChange={(filename) => this.setState({ filename })}
            onAddRow={this.addRow}
            onSave={() => this.submit(s.rows)}
            onClear={this.clear}
          />
          {s.error && <Alert severity="error">{s.error}</Alert>}
          {s.success && <Alert severity="success">{s.success}</Alert>}
          <BieEntryDataGrid
            rows={rows}
            lookups={lookups}
            loading={loading}
            onChange={(rows) => this.setState({ rows })}
            onSubmit={this.submit}
          />
        </Stack>
      </Box>
    );
  }
}
export default BieEntryWorkbench;
